import { IBaseCompany } from '../../Auth/types/company.types';
import { ICompanyUser } from '../../Auth/types/companyUser.types';
import { IRawMaterial } from '../../ComponentChain/types/component.types';
import { TLanguage } from 'DesignSystem/types/language.types';
import { TBasicLogAction, TLogSource } from '../../../types/log.types';
import { ImageVariant } from '../../Media/types/media.types';
import { IBasePartnerCompany } from '../../Partner/types/partner.types';
import { ICoordinates } from 'types/map.types';

export enum ESiteType {
  FARM = 'FARM',
  MINE = 'MINE',
  FOREST = 'FOREST',
  COLLECTION_SITE = 'COLLECTION_SITE',
  ORIGIN_SITE = 'ORIGIN_SITE',
  PLANT = 'PLANT',
  PROCESSING_SITE = 'PROCESSING_SITE',
  FACTORY = 'FACTORY',
  MANUFACTURING_SITE = 'MANUFACTURING_SITE',
  WAREHOUSE = 'WAREHOUSE',
  HANDLING_SITE = 'HANDLING_SITE',
  GROCERY_STORE = 'GROCERY_STORE',
  DISTRIBUTION_SITE = 'DISTRIBUTION_SITE',
  GENERIC_SITE = 'GENERIC_SITE',
  CLUSTER = 'CLUSTER',
}

export type TSiteType = `${ESiteType}`;
export type TSiteTypeWithoutCluster = Exclude<ESiteType, ESiteType.CLUSTER>;

export enum ELandOwnership {
  PRIVATE_OWNERSHIP = 'PRIVATE_OWNERSHIP',
  PUBLIC_OWNERSHIP = 'PUBLIC_OWNERSHIP',
  COMMUNAL_OWNERSHIP = 'COMMUNAL_OWNERSHIP',
  STATE_OWNERSHIP = 'STATE_OWNERSHIP',
  TENANCY = 'TENANCY',
  CONCESSION = 'CONCESSION',
  EASEMENT = 'EASEMENT',
  COLLECTIVE_FARMS = 'COLLECTIVE_FARMS',
  TRUSTS = 'TRUSTS',
  COMMON_PROPERTY_REGIMES = 'COMMON_PROPERTY_REGIMES',
  ADVERSE_POSSESSION = 'ADVERSE_POSSESSION',
  LAND_COOPERATIVES = 'LAND_COOPERATIVES',
  USUFRUCT_RIGHTS = 'USUFRUCT_RIGHTS',
  OTHER = 'OTHER',
}

export type TLandOwnership = `${ELandOwnership}`;

export enum ESiteViewOption {
  LIST = 'LIST',
  CLUSTER = 'CLUSTER',
}

export type TSiteViewOption = `${ESiteViewOption}`;

export enum TCoordinateFeature {
  UNKOWN = 'UNKOWN',
  COUNTRY = 'COUNTRY',
  REGION = 'REGION',
  POSTCODE = 'POSTCODE',
  DISTRICT = 'DISTRICT',
  PLACE = 'PLACE',
  LOCALITY = 'LOCALITY',
  NEIGHBORHOOD = 'NEIGHBORHOOD',
  STREET = 'STREET',
  ADDRESS = 'ADDRESS',
}

export interface ICoordinateFeature {
  featureType: TCoordinateFeature;
  locationName: string;
  center: ICoordinates;
}

export type ISiteLog = {
  id: string;
  comment: string;
  source: TLogSource;
  timestamp: Date;
  action: TBasicLogAction;
} & (
  | { user: never; company: never; userEmail: string }
  | { user: ICompanyUser; company: IBaseCompany; userEmail: never }
);

export interface IBaseDefaultSite {
  id: string;
  title: string;
  lang: TLanguage;
  createdBy: IBasePartnerCompany;
  ownedBy?: IBasePartnerCompany;
  locationName: string;
  locationCoordinates: ICoordinates;
  image?: ImageVariant | null;
  modifiedTimestamp: Date;
  createdTimestamp: Date;
  externalId?: string;
  description?: string;
  coordinateFeatures: ICoordinateFeature[];
  inUse: boolean;
  countryCode: string;
}

export interface IDefaultSite extends IBaseDefaultSite {
  logs: ISiteLog[];
}

export type IBaseGenericSite = IBaseDefaultSite & {
  siteType: ESiteType.GENERIC_SITE;
  siteClusterId?: string;
};
export type IGenericSite = IDefaultSite & IBaseGenericSite;

export type IBaseMineSite = IBaseDefaultSite & { siteType: ESiteType.MINE; siteClusterId?: string };
export type IMineSite = IDefaultSite & IBaseMineSite;

export type IBaseForestSite = IBaseDefaultSite & {
  siteType: ESiteType.FOREST;
  siteClusterId?: string;
};
export type IForestSite = IDefaultSite & IBaseForestSite;

export type IBaseCollectionSite = IBaseDefaultSite & {
  siteType: ESiteType.COLLECTION_SITE;
  siteClusterId?: string;
};
export type ICollectionSite = IDefaultSite & IBaseCollectionSite;

export type IBaseOriginSite = IBaseDefaultSite & {
  siteType: ESiteType.ORIGIN_SITE;
  siteClusterId?: string;
};
export type IOriginSite = IDefaultSite & IBaseOriginSite;

export type IBasePlantSite = IBaseDefaultSite & {
  siteType: ESiteType.PLANT;
  siteClusterId?: string;
};
export type IPlantSite = IDefaultSite & IBasePlantSite;

export type IBaseProcessingSite = IBaseDefaultSite & {
  siteType: ESiteType.PROCESSING_SITE;
  siteClusterId?: string;
};
export type IProcessingSite = IDefaultSite & IBaseProcessingSite;

export type IBaseFactorySite = IBaseDefaultSite & {
  siteType: ESiteType.FACTORY;
  siteClusterId?: string;
};
export type IFactorySite = IDefaultSite & IBaseFactorySite;

export type IBaseManufacturingSite = IBaseDefaultSite & {
  siteType: ESiteType.MANUFACTURING_SITE;
  siteClusterId?: string;
};
export type IManufacturingSite = IDefaultSite & IBaseManufacturingSite;

export type IBaseWarehouseSite = IBaseDefaultSite & {
  siteType: ESiteType.WAREHOUSE;
  siteClusterId?: string;
};
export type IWarehouseSite = IDefaultSite & IBaseWarehouseSite;

export type IBaseHandlingSite = IBaseDefaultSite & {
  siteType: ESiteType.HANDLING_SITE;
  siteClusterId?: string;
};
export type IHandlingSite = IDefaultSite & IBaseHandlingSite;

export type IBaseGroceryStoreSite = IBaseDefaultSite & {
  siteType: ESiteType.GROCERY_STORE;
  siteClusterId?: string;
};
export type IGroceryStoreSite = IDefaultSite & IBaseGroceryStoreSite;

export type IBaseDistributionSite = IBaseDefaultSite & {
  siteType: ESiteType.DISTRIBUTION_SITE;
  siteClusterId?: string;
};
export type IDistributionSite = IDefaultSite & IBaseDistributionSite;

export interface IBaseFarmSite extends IBaseDefaultSite {
  siteType: ESiteType.FARM;
  siteClusterId?: string;
  sizeHa?: number;
  rawMaterial: IRawMaterial;
  numberOfFarmers?: number;
  additionalRawMaterialIds?: string[];
  landOwnership?: TLandOwnership;
  farmOwner?: string;
  plotsCount: number;
  plotsSizeHa: number;
}
export type IFarmSite = IDefaultSite & IBaseFarmSite;

export type TBaseSiteTypesWithoutCluster =
  | IBaseFarmSite
  | IBaseGenericSite
  | IBaseMineSite
  | IBaseForestSite
  | IBaseCollectionSite
  | IBaseOriginSite
  | IBasePlantSite
  | IBaseProcessingSite
  | IBaseFactorySite
  | IBaseManufacturingSite
  | IBaseWarehouseSite
  | IBaseHandlingSite
  | IBaseGroceryStoreSite
  | IBaseDistributionSite;

export type TSiteTypesWithoutCluster =
  | IFarmSite
  | IGenericSite
  | IMineSite
  | IForestSite
  | ICollectionSite
  | IOriginSite
  | IPlantSite
  | IProcessingSite
  | IFactorySite
  | IManufacturingSite
  | IWarehouseSite
  | IHandlingSite
  | IGroceryStoreSite
  | IDistributionSite;

/**
 * This is a union type of all the different site types that can be part of a cluster.
 */
export type TClusterSitesType<SitesType extends TSiteTypeWithoutCluster> =
  SitesType extends ESiteType.FARM
    ? IFarmSite
    : SitesType extends ESiteType.GENERIC_SITE
    ? IGenericSite
    : SitesType extends ESiteType.MINE
    ? IMineSite
    : SitesType extends ESiteType.FOREST
    ? IForestSite
    : SitesType extends ESiteType.COLLECTION_SITE
    ? ICollectionSite
    : SitesType extends ESiteType.ORIGIN_SITE
    ? IOriginSite
    : SitesType extends ESiteType.PLANT
    ? IPlantSite
    : SitesType extends ESiteType.PROCESSING_SITE
    ? IProcessingSite
    : SitesType extends ESiteType.FACTORY
    ? IFactorySite
    : SitesType extends ESiteType.MANUFACTURING_SITE
    ? IManufacturingSite
    : SitesType extends ESiteType.WAREHOUSE
    ? IWarehouseSite
    : SitesType extends ESiteType.HANDLING_SITE
    ? IHandlingSite
    : SitesType extends ESiteType.GROCERY_STORE
    ? IGroceryStoreSite
    : SitesType extends ESiteType.DISTRIBUTION_SITE
    ? IDistributionSite
    : never;

export interface IBaseClusterSite<SitesType extends TSiteTypeWithoutCluster = ESiteType.FARM>
  extends IBaseDefaultSite {
  siteType: ESiteType.CLUSTER;
  // Can be undefined if the cluster is empty
  sitesType?: SitesType;
  // The site cluster raw material of the farms that are part of the cluster if of sitesType FARM
  sitesRawMaterial?: IRawMaterial;
  sitesCount: number;
  plotsCount: number;
  plotsSizeHa: number;
}
export interface IClusterSite<SitesType extends TSiteTypeWithoutCluster = ESiteType.FARM>
  extends IDefaultSite,
    IBaseClusterSite<SitesType> {
  sites: {
    edges: {
      node: TClusterSitesType<SitesType>;
    }[];
  };
}

export type IBaseSite = TBaseSiteTypesWithoutCluster | IBaseClusterSite;

export type ISite = TSiteTypesWithoutCluster | IClusterSite;
